import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Plus, PencilSimple, Trash, Buildings, MapPin, Star } from '@phosphor-icons/react';
import ReportToolbar from '../../../components/ReportToolbar';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WH_TYPES = [
  { value: 'main', label: 'Main' },
  { value: 'branch', label: 'Branch' },
  { value: 'site', label: 'Site' },
  { value: 'van', label: 'Van / Mobile' },
  { value: 'other', label: 'Other' },
];

export default function WarehousesTab() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: '', name: '', type: 'site', address: '', manager_name: '', is_active: true,
  });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/inventory/warehouses`);
      setWarehouses(res.data);
    } catch (e) {
      toast.error('Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openNew = () => {
    setEditing(null);
    setForm({ code: '', name: '', type: 'site', address: '', manager_name: '', is_active: true });
    setShowForm(true);
  };
  const openEdit = (w) => {
    setEditing(w);
    setForm({
      code: w.code || '', name: w.name || '', type: w.type || 'main',
      address: w.address || '', manager_name: w.manager_name || '', is_active: w.is_active !== false,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error('Code and name are required');
      return;
    }
    try {
      if (editing) {
        await axios.patch(`${API_URL}/inventory/warehouses/${editing.id}`, form);
        toast.success('Warehouse updated');
      } else {
        await axios.post(`${API_URL}/inventory/warehouses`, form);
        toast.success('Warehouse created');
      }
      setShowForm(false);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const setAsDefault = async (w) => {
    try {
      await axios.patch(`${API_URL}/inventory/warehouses/${w.id}`, { is_default: true });
      toast.success(`${w.name} is now the default warehouse`);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set default');
    }
  };

  const del = async (w) => {
    if (!window.confirm(`Delete warehouse "${w.name}"? This is allowed only if it holds no stock.`)) return;
    try {
      await axios.delete(`${API_URL}/inventory/warehouses/${w.id}`);
      toast.success('Warehouse deleted');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card className="p-4" data-testid="warehouses-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Buildings size={20} weight="duotone" />
            Warehouses & Storage Locations
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Add your physical stores, site stores and vans. Stock balances are tracked per warehouse.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-warehouse-btn">
          <Plus size={16} className="mr-2" /> New Warehouse
        </Button>
      </div>

      <ReportToolbar
        testidPrefix="warehouses-toolbar"
        downloadBaseName="warehouses"
        exportUrl={(fmt) => `${API_URL}/reports/warehouses.${fmt}`}
      />

      {loading ? (
        <div className="py-12 text-center text-muted-foreground">Loading…</div>
      ) : warehouses.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">No warehouses yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left px-3 py-2 font-semibold uppercase text-xs tracking-wide">Code</th>
                <th className="text-left px-3 py-2 font-semibold uppercase text-xs tracking-wide">Name</th>
                <th className="text-left px-3 py-2 font-semibold uppercase text-xs tracking-wide">Type</th>
                <th className="text-left px-3 py-2 font-semibold uppercase text-xs tracking-wide">Manager</th>
                <th className="text-left px-3 py-2 font-semibold uppercase text-xs tracking-wide">Status</th>
                <th className="text-right px-3 py-2 font-semibold uppercase text-xs tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {warehouses.map(w => (
                <tr key={w.id} className="hover:bg-muted/30" data-testid={`warehouse-row-${w.id}`}>
                  <td className="px-3 py-2 font-mono">{w.code}</td>
                  <td className="px-3 py-2 font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={14} className="text-muted-foreground" />
                      {w.name}
                      {w.is_default && (
                        <Badge variant="outline" className="ml-1 bg-amber-50 text-amber-700 border-amber-300 text-[10px]">
                          <Star size={10} weight="fill" className="mr-0.5" /> Default
                        </Badge>
                      )}
                    </span>
                    {w.address && <div className="text-xs text-muted-foreground mt-0.5">{w.address}</div>}
                  </td>
                  <td className="px-3 py-2 capitalize">{w.type}</td>
                  <td className="px-3 py-2">{w.manager_name || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2">
                    {w.is_active
                      ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">Active</Badge>
                      : <Badge variant="outline" className="bg-slate-100 text-slate-600">Inactive</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    {!w.is_default && (
                      <Button size="sm" variant="ghost" onClick={() => setAsDefault(w)} title="Set as default"
                              data-testid={`default-wh-${w.id}`}>
                        <Star size={14} />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(w)} data-testid={`edit-wh-${w.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    {!w.is_default && (
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                              onClick={() => del(w)} data-testid={`delete-wh-${w.id}`}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md" data-testid="warehouse-form">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit warehouse' : 'New warehouse'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Code *</Label>
              <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                     placeholder="e.g. MAIN, JBR, VAN1" data-testid="wh-form-code" />
            </div>
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Main Store, Site Store - JBR" data-testid="wh-form-name" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="wh-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WH_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Manager</Label>
              <Input value={form.manager_name} onChange={e => setForm({ ...form, manager_name: e.target.value })}
                     placeholder="(optional)" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                     placeholder="(optional)" />
            </div>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })}
                     className="h-4 w-4 rounded border-border" />
              Active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} data-testid="wh-form-save">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
